import { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
// Özel Durum takvimi: haftalık (saat ızgarası) ve aylık görünüm
export const DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
export const PALETTE = ['#d97706', '#dc2626', '#2563eb', '#059669', '#7c3aed', '#db2777', '#0891b2', '#65a30d'];
const HOUR_PX = 40;
export const IN_REVIEW = ['isg_inceleme', 'onay_bekliyor', 'talep_bilgisi_hatali'];

export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const startOfWeek = (d) => addDays(new Date(d.getFullYear(), d.getMonth(), d.getDate()), -((d.getDay() + 6) % 7));
const mins = (t) => { const [h, m] = (t || '00:00').split(':').map(Number); return h * 60 + (m || 0); };
const onDay = (ev, day) => ev.start_date <= day && ev.end_date >= day;

const situationLabel = (ev) => ev.special_situations.map(x => x.title).join(', ');


const lanes = (items) => {
  // Aynı saatte çakışan talepler yan yana dizilir
  const sorted = [...items].sort((a, b) => a.s - b.s);
  const ends = [];
  sorted.forEach(it => { let i = ends.findIndex(e => e <= it.s); if (i < 0) { i = ends.length; ends.push(0); } ends[i] = it.e; it.lane = i; });
  sorted.forEach(it => { it.lanes = ends.length; });
  return sorted;
};

const defaultWeek = (label) => (ev) => [`${ev.start_time}–${ev.end_time} · ${ev.location_name}`, label(ev), ev.company_name];
const defaultMonth = (label) => (ev) => [`${ev.start_time} ${ev.location_name} · ${label(ev)}`];

const EventTip = ({ tip, ev, children }) => (tip ? (
  <Tooltip delayDuration={250}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side="right" className="max-w-xs p-0" data-testid={`calendar-tip-${ev.id}`}>{tip(ev)}</TooltipContent>
  </Tooltip>
) : children);

const dayEvents = (events, d) => events.filter(ev => onDay(ev, d)).sort((a, b) => mins(a.start_time) - mins(b.start_time));
const dow = (d) => DAYS[(new Date(`${d}T00:00:00`).getDay() + 6) % 7];

// Mobil kart: pastel zemin, koyu yazı, solda 4px durum çizgisi (onaylı: #E8F5E9 / #166534 / #22A653)
const GREEN = ['#16a34a', '#22c55e', '#22a653'];
export const agendaStyle = (ev, c) => {
  const green = GREEN.includes((c || '').toLowerCase());
  const line = green ? '#22A653' : c;
  return {
    backgroundColor: green ? '#E8F5E9' : `color-mix(in srgb, ${c} 12%, white)`,
    color: green ? '#166534' : `color-mix(in srgb, ${c} 62%, black)`,
    border: `1px ${IN_REVIEW.includes(ev.display_status || ev.status) ? 'dashed' : 'solid'} color-mix(in srgb, ${line} 35%, white)`,
    borderLeft: `4px solid ${line}`,
  };
};

// Mobil: bir günün talepleri (haftalık listede ve aylık görünümde seçili gün için)
const DayAgenda = ({ d, events, colorOf, onPick, today, lines }) => {
  const list = dayEvents(events, d);
  return (
    <div className={`px-3 py-2.5 ${d === today ? 'bg-amber-50/60' : ''}`} data-testid={`calendar-agenda-day-${d}`}>
      <p className={`mb-1.5 text-xs font-semibold ${d === today ? 'text-amber-700' : 'text-slate-600'}`}>{dow(d)} {d.slice(8, 10)}.{d.slice(5, 7)}{d === today && ' · Bugün'} <span className="font-normal text-slate-400">({list.length})</span></p>
      {list.length === 0 ? <p className="text-xs text-slate-300">Talep yok</p> : (
        <div className="space-y-1.5">
          {list.map(ev => (
            <button key={ev.id} type="button" onClick={() => onPick(ev)} data-testid={`calendar-agenda-event-${ev.id}-${d}`}
              className="block w-full rounded-lg px-3 py-2 text-left text-[13px] leading-snug shadow-sm" style={agendaStyle(ev, colorOf(ev))}>
              {lines(ev).filter(Boolean).slice(0, 3).map((t, j) => <span key={j} className={`block break-words ${j === 0 ? 'text-sm font-bold' : 'font-medium opacity-90'}`}>{t}</span>)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Mobil: haftalık görünüm gün gün liste (saat ızgarası dar ekranda okunmaz)
const WeekAgenda = ({ days, ...rest }) => (
  <div className="divide-y rounded-lg border bg-white sm:hidden" data-testid="calendar-week-agenda">
    {days.map(d => <DayAgenda key={d} d={d} {...rest} />)}
  </div>
);

// Mobil: kompakt ay (renkli noktalar + sayı), dokunulan günün talepleri altta
const MonthCompact = ({ days, month, events, colorOf, today, ...rest }) => {
  const [picked, setPicked] = useState(null);
  const inMonth = (d) => Number(d.slice(5, 7)) === month + 1;
  const sel = picked && days.includes(picked) ? picked : (days.includes(today) && inMonth(today) ? today : days.find(inMonth));
  return (
    <div className="space-y-3 sm:hidden" data-testid="calendar-month-compact">
      <div className="rounded-lg border bg-white">
        <div className="grid grid-cols-7 border-b bg-slate-50">{DAYS.map(d => <div key={d} className="py-1.5 text-center text-[11px] font-medium text-slate-500">{d}</div>)}</div>
        <div className="grid grid-cols-7">
          {days.map(d => {
            const list = dayEvents(events, d);
            const on = d === sel;
            return (
              <button key={d} type="button" onClick={() => setPicked(d)} data-testid={`calendar-compact-day-${d}`} aria-pressed={on}
                className={`flex !min-h-[52px] flex-col items-center justify-start gap-1 border-b border-r pt-1.5 transition-colors ${!inMonth(d) ? 'text-slate-300' : 'text-slate-700'} ${on ? 'bg-slate-900 !text-white' : d === today ? 'bg-amber-50' : ''}`}>
                <span className={`text-xs font-medium ${d === today && !on ? 'text-amber-700' : ''}`}>{Number(d.slice(8, 10))}</span>
                {list.length > 0 && (
                  <span className="flex items-center gap-0.5">
                    {list.slice(0, 3).map(ev => <span key={ev.id} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorOf(ev) }} />)}
                    {list.length > 3 && <span className={`text-[9px] leading-none ${on ? 'text-slate-200' : 'text-slate-500'}`}>+{list.length - 3}</span>}
                  </span>
                )}
                {list.length > 0 && <span className={`text-[9px] leading-none ${on ? 'text-slate-300' : 'text-slate-400'}`}>{list.length} talep</span>}
              </button>
            );
          })}
        </div>
      </div>
      {sel && <div className="rounded-lg border bg-white"><DayAgenda d={sel} events={events} colorOf={colorOf} today={today} {...rest} /></div>}
    </div>
  );
};

export const WeekView = ({ days, events, colorOf, onPick, today, styleOf = agendaStyle, label = situationLabel, weekLines, tip }) => {
  const lines = weekLines || defaultWeek(label);
  return (
  <TooltipProvider>
  <WeekAgenda days={days} events={events} colorOf={colorOf} onPick={onPick} today={today} styleOf={styleOf} lines={lines} />
  <div className="hidden border rounded-lg overflow-hidden bg-white sm:block" data-testid="calendar-week-view">
    <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b bg-slate-50">
      <div />
      {days.map((d, i) => (
        <div key={d} className={`px-2 py-2 text-center text-xs font-medium border-l ${d === today ? 'text-amber-700 bg-amber-50' : 'text-slate-600'}`}>
          {DAYS[i]} <span className="font-semibold">{d.slice(8, 10)}.{d.slice(5, 7)}</span>
        </div>
      ))}
    </div>
    <div>
      <div className="grid grid-cols-[56px_repeat(7,1fr)] relative" style={{ height: HOUR_PX * 24 }}>
        <div className="relative">{Array.from({ length: 24 }, (_, h) => <span key={h} className="absolute right-2 text-[10px] text-slate-400" style={{ top: Math.max(h * HOUR_PX - 6, 0) }}>{String(h).padStart(2, '0')}:00</span>)}</div>
        {days.map(d => {
          const items = lanes(events.filter(ev => onDay(ev, d)).flatMap(ev => {
            const s = mins(ev.start_time); const e = mins(ev.end_time);
            if (e > s) return [{ ev, s, e, key: ev.id }];
            // Gece çalışması: akşam başlar, ertesi sabah biter
            return [{ ev, s, e: 24 * 60, key: ev.id }, ...(d > ev.start_date && e > 0 ? [{ ev, s: 0, e, key: `${ev.id}-m` }] : [])];
          }));
          return (
            <div key={d} className={`relative border-l ${d === today ? 'bg-amber-50/30' : ''}`}>
              {Array.from({ length: 24 }, (_, h) => <div key={h} className="border-b border-slate-100" style={{ height: HOUR_PX }} />)}
              {items.map(({ ev, s, e, key, lane, lanes: n }) => (
                <EventTip key={key} tip={tip} ev={ev}>
                <button key={key} type="button" onClick={() => onPick(ev)} data-testid={`calendar-event-${key}-${d}`}
                  className="absolute flex flex-col justify-start rounded-md border px-1.5 py-1 text-left text-[11px] leading-tight overflow-hidden shadow-sm transition-transform hover:scale-[1.02] hover:z-10"
                  style={{ top: (s / 60) * HOUR_PX, height: Math.max(((e - s) / 60) * HOUR_PX - 2, 22), left: `calc(${(lane / n) * 100}% + 2px)`, width: `calc(${100 / n}% - 4px)`, ...styleOf(ev, colorOf(ev)) }}>
                  {lines(ev).filter(Boolean).map((t, i) => <p key={i} className={`truncate ${i === 0 ? 'font-semibold' : i > 2 ? 'opacity-80' : ''}`}>{t}</p>)}
                </button>
                </EventTip>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  </div>
  </TooltipProvider>
  );
};

export const MonthView = ({ days, month, events, colorOf, onPick, today, styleOf = agendaStyle, label = situationLabel, monthLines, tip }) => {
  const lines = monthLines || defaultMonth(label);
  return (
  <TooltipProvider>
  <MonthCompact days={days} month={month} events={events} colorOf={colorOf} onPick={onPick} today={today} styleOf={styleOf} lines={lines} />
  <div className="hidden border rounded-lg overflow-hidden bg-white sm:block" data-testid="calendar-month-view">
    <div>
    <div className="grid grid-cols-7 bg-slate-50 border-b">{DAYS.map(d => <div key={d} className="px-2 py-2 text-xs font-medium text-slate-600 text-center">{d}</div>)}</div>
    <div className="grid grid-cols-7">
      {days.map(d => {
        const list = events.filter(ev => onDay(ev, d));
        const other = Number(d.slice(5, 7)) !== month + 1;
        return (
          <div key={d} className={`min-h-[110px] border-b border-r p-1.5 space-y-1 ${other ? 'bg-slate-50/70 text-slate-400' : ''} ${d === today ? 'bg-amber-50/50' : ''}`} data-testid={`calendar-day-${d}`}>
            <p className={`text-xs font-medium ${d === today ? 'text-amber-700' : ''}`}>{Number(d.slice(8, 10))}</p>
            {list.slice(0, 3).map(ev => (
              <EventTip key={ev.id} tip={tip} ev={ev}>
              <button type="button" onClick={() => onPick(ev)} data-testid={`calendar-chip-${ev.id}-${d}`}
                className="block w-full rounded border px-1.5 py-0.5 text-left text-[11px] leading-snug transition-opacity hover:opacity-90" style={styleOf(ev, colorOf(ev))}>
                {lines(ev).filter(Boolean).map((t, i) => <span key={i} className={`block truncate ${i === 0 ? 'font-medium' : 'opacity-90'}`}>{t}</span>)}
              </button>
              </EventTip>
            ))}
            {list.length > 3 && <p className="text-[11px] text-slate-500">+{list.length - 3} talep daha</p>}
          </div>
        );
      })}
    </div>
    </div>
  </div>
  </TooltipProvider>
  );
};

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

export const useCalendarRange = (view, anchor) => useMemo(() => {
  const first = view === 'week' ? startOfWeek(anchor) : startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  return Array.from({ length: view === 'week' ? 7 : 42 }, (_, i) => iso(addDays(first, i)));
}, [view, anchor]);

export const CalendarToolbar = ({ view, setView, anchor, setAnchor, days, children }) => {
  const move = (n) => setAnchor(view === 'week' ? addDays(anchor, 7 * n) : new Date(anchor.getFullYear(), anchor.getMonth() + n, 1));
  const title = view === 'week' ? `${days[0].slice(8, 10)}.${days[0].slice(5, 7)} – ${days[6].slice(8, 10)}.${days[6].slice(5, 7)}.${days[6].slice(0, 4)}` : `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-1">
        <Button size="icon" variant="outline" onClick={() => move(-1)} data-testid="calendar-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={() => setAnchor(new Date())} data-testid="calendar-today-btn">Bugün</Button>
        <Button size="icon" variant="outline" onClick={() => move(1)} data-testid="calendar-next-btn"><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <p className="font-semibold text-slate-800 flex items-center gap-2 sm:min-w-[180px]" data-testid="calendar-title"><CalendarDays className="h-4 w-4 text-amber-600" />{title}</p>
      <div className="flex rounded-lg border p-0.5 bg-slate-50">
        {[['week', 'Haftalık'], ['month', 'Aylık']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setView(k)} data-testid={`calendar-view-${k}`} aria-pressed={view === k}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors ${view === k ? 'bg-orange-500 font-semibold text-white shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-900'}`}>{l}</button>
        ))}
      </div>
      <div className="grid w-full grid-cols-2 gap-2 sm:ml-auto sm:flex sm:w-auto sm:flex-wrap [&>button]:w-full sm:[&>button]:w-auto">{children}</div>
    </div>
  );
};
